// === Countries (from public.countries) ===
// Every supported country — name, currency, locale, sales tax, default
// timezone — comes from GET /api/v1/admin/countries, so adding a country is a
// database row, not a code change. Loaded once per token and shared by every
// screen (Country select, catalogue switch, currency labels).

import { useEffect, useState } from 'react';
import { useAuth } from './auth';

export interface Country {
  readonly code: string;
  readonly name: string;
  readonly currency_code: string;
  readonly currency_symbol: string;
  readonly locale: string;
  readonly tax_label: string;
  /** Fraction, e.g. 0.1 = 10%. */
  readonly tax_rate: number;
  readonly default_timezone: string | null;
  readonly active: boolean;
}

function apiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

let cache: { token: string; promise: Promise<Country[]> } | null = null;

function load(token: string): Promise<Country[]> {
  if (cache?.token === token) return cache.promise;
  const promise = fetch(`${apiUrl()}/api/v1/admin/countries`, { headers: { Authorization: `Bearer ${token}` } })
    .then(async (res) => {
      if (!res.ok) throw new Error(`Countries request failed (${res.status})`);
      return ((await res.json()) as { data: Country[] }).data;
    });
  cache = { token, promise };
  promise.catch(() => { if (cache?.promise === promise) cache = null; });
  return promise;
}

/** All countries (active and inactive). Empty until loaded. */
export function useCountries(): readonly Country[] {
  const { session } = useAuth();
  const token = session?.access_token ?? '';
  const [countries, setCountries] = useState<readonly Country[]>([]);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    load(token)
      .then((rows) => { if (!cancelled) setCountries(rows); })
      .catch((e: unknown) => { console.error('[countries]', e); });
    return () => { cancelled = true; };
  }, [token]);
  return countries;
}

export function findCountry(countries: readonly Country[], code: string): Country | undefined {
  return countries.find((c) => c.code === code);
}

/** "AUD ($) · GST 10%" style summary for a country's defaults. */
export function formatTaxPercent(rate: number): string {
  return `${Math.round(rate * 10000) / 100}%`;
}
